import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../data/supabase';
import type { OrgRow } from '../../data/database.types';
import { qk } from '../../data/queries';
import { useToast } from '../../ui/toast';

/**
 * Spec 5.13: a notice when another user publishes or regenerates. The org
 * row's two pointers change on exactly those events; its audit trigger
 * records who did it, so our own actions are recognised and stay silent.
 */
export function useOrgRealtime(orgId: string, myUserId: string | null) {
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    const channel = supabase
      .channel(`org-${orgId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'orgs', filter: `id=eq.${orgId}` }, (payload) => {
        const next = payload.new as Partial<OrgRow>;
        const cur = qc.getQueryData<OrgRow>(qk.org(orgId));
        const mine = !!myUserId && next.updated_by === myUserId;
        if (cur && !mine) {
          if (next.current_version_id && next.current_version_id !== cur.current_version_id) {
            toast.show({ message: 'Someone else just published a new version. Views refresh automatically.', tone: 'warn', duration: 10_000 });
          } else if (next.current_draft_run_id && next.current_draft_run_id !== cur.current_draft_run_id) {
            toast.show({ message: 'Someone else just regenerated the draft. Views refresh automatically.', tone: 'warn', duration: 10_000 });
          }
        }
        if (cur) qc.setQueryData<OrgRow>(qk.org(orgId), { ...cur, ...next });
        void qc.invalidateQueries({ queryKey: qk.org(orgId) });
        void qc.invalidateQueries({ queryKey: ['schedule_run'] });
        void qc.invalidateQueries({ queryKey: ['schedule_assignments'] });
        void qc.invalidateQueries({ queryKey: ['schedule_versions'] });
        void qc.invalidateQueries({ queryKey: ['schedule_version'] });
        void qc.invalidateQueries({ queryKey: ['changes_since'] });
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [orgId, myUserId, qc, toast]);
}
